"use client";

import { useEffect, useState } from "react";
import AuthenticatedLayout from "../../Layouts/AuthenticatedLayout";
import InfoSection from "../../Components/InfoSection";
import GroupedContainer from "../../Components/GroupedContainer";
import ConfirmationDialog from "../../Components/ConfirmationDialog";
import SecondaryButton from "../../Components/SecondaryButton";
import PrimaryButton from "../../Components/PrimaryButton";
import { Dialog, DialogContent, DialogTitle, DialogTrigger } from "../../Components/ui/dialog";
import axios from "axios";
import { toast } from "sonner";

import { TbSearch, TbX, TbFilter, TbChevronDown, TbTrash, TbPlus, TbEye, TbInfoCircle } from "react-icons/tb";
import { ChevronLeft, ChevronRight } from "lucide-react";

export default function Shippers() {
  const API_BASE = "https://new.cajuu.app/api/shipper";

  // ✅ backend-driven list
  const [shippers, setShippers] = useState([]);
  const [loading, setLoading] = useState(false);

  // ✅ pagination
  const [perPage, setPerPage] = useState(20); // ✅ default 20
  const [currentPage, setCurrentPage] = useState(1);
  const [totalPages, setTotalPages] = useState(1);
  const [paginationMeta, setPaginationMeta] = useState({
    total: 0,
    from: 0,
    to: 0,
    per_page: 20,
  });

  // ✅ universal search
  const [searchQuery, setSearchQuery] = useState("");
  const [debouncedSearchQuery, setDebouncedSearchQuery] = useState("");

  // ✅ sorting
  const [sortBy, setSortBy] = useState("shipper_name");
  const [sortDirection, setSortDirection] = useState("asc");

  // ✅ modal
  const [isDialogOpen, setIsDialogOpen] = useState(false);
  const [dialogMode, setDialogMode] = useState("create");

  const [isSubmitting, setIsSubmitting] = useState(false);

  const [data, setData] = useState({
    id: null,
    shipper_name: "",
    type: "",
    phone: "",
    accounting_email: "",
    info_email: "",
    address: "",
    city: "",
    state: "",
    zip_code: "",
    address_line_1: "",
    address_line_2: "",
  });

  const extractErrorMessage = (error) => {
    try {
      const resData = error?.response?.data;

      if (resData?.errors && typeof resData.errors === "object") {
        const firstKey = Object.keys(resData.errors)[0];
        const firstMsg = Array.isArray(resData.errors[firstKey])
          ? resData.errors[firstKey][0]
          : resData.errors[firstKey];
        if (firstMsg) return String(firstMsg);
      }

      if (resData?.message) return String(resData.message);
      if (typeof resData?.data === "string") return String(resData.data);

      return "An error occurred. Please try again later.";
    } catch (e) {
      return "An error occurred. Please try again later.";
    }
  };

  // ✅ debounce search
  useEffect(() => {
    const timer = setTimeout(() => setDebouncedSearchQuery(searchQuery), 450);
    return () => clearTimeout(timer);
  }, [searchQuery]);

  const formatAddress = (shipper) => {
    const hasLines =
      (shipper.address_line_1 && shipper.address_line_1.trim() !== "") ||
      (shipper.address_line_2 && shipper.address_line_2.trim() !== "");

    if (hasLines) {
      const a1 = shipper.address_line_1 ? shipper.address_line_1.trim() : "";
      const a2 = shipper.address_line_2 ? shipper.address_line_2.trim() : "";
      return [a1, a2].filter(Boolean).join(", ");
    }

    const address = shipper.address ? shipper.address.trim() : "";
    const city = shipper.city ? shipper.city.trim() : "";
    const state = shipper.state ? shipper.state.trim() : "";
    const zip = shipper.zip_code ? shipper.zip_code.trim() : "";

    const line1 = address;
    const line2 = [city, state].filter(Boolean).join(", ");
    const line3 = zip;

    return [line1, line2, line3].filter((v) => v && v !== "").join(" ");
  };

  const fetchShippers = async (pageToFetch = currentPage) => {
    setLoading(true);
    try {
      const token = localStorage.getItem("token");

      const params = {
        page: pageToFetch,
        per_page: perPage,
        sort_by: sortBy,
        sort_direction: sortDirection,
      };

      const q = (debouncedSearchQuery || "").trim();
      if (q) params.q = q;

      const response = await axios.get(`${API_BASE}/paginated`, {
        params,
        headers: {
          "Screen-Name": "/Shipper",
          Authorization: `Bearer ${token}`,
        },
      });

      const result = response.data;
      const paginator = result?.data || {};
      const list = Array.isArray(paginator?.data) ? paginator.data : [];

      const apiLastPage = parseInt(paginator?.last_page || 1);
      const apiCurrentPage = parseInt(paginator?.current_page || pageToFetch);

      setTotalPages(apiLastPage || 1);
      setPaginationMeta({
        total: parseInt(paginator?.total || 0),
        from: parseInt(paginator?.from || 0) || 0,
        to: parseInt(paginator?.to || 0) || 0,
        per_page: parseInt(paginator?.per_page || perPage) || perPage,
      });

      if (apiLastPage > 0 && pageToFetch > apiLastPage) {
        setCurrentPage(apiLastPage);
      } else {
        setCurrentPage(apiCurrentPage);
      }

      setShippers(list);
    } catch (error) {
      setShippers([]);
      setTotalPages(1);
      setPaginationMeta({ total: 0, from: 0, to: 0, per_page: perPage });
      toast.error(extractErrorMessage(error));
    } finally {
      setLoading(false);
    }
  };

  // ✅ refetch on filters/page change (backend-driven)
  useEffect(() => {
    fetchShippers(currentPage);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [currentPage, debouncedSearchQuery, perPage, sortBy, sortDirection]);

  const resetForm = () => {
    setData({
      id: null,
      shipper_name: "",
      type: "",
      phone: "",
      accounting_email: "",
      info_email: "",
      address: "",
      city: "",
      state: "",
      zip_code: "",
      address_line_1: "",
      address_line_2: "",
    });
  };

  const validateCreate = () => {
    if (!data.shipper_name || data.shipper_name.trim() === "") {
      toast.error("Please fill in all required fields.");
      return false;
    }

    const hasFullAddress =
      (data.address && data.address.trim() !== "") &&
      (data.city && data.city.trim() !== "") &&
      (data.state && data.state.trim() !== "") &&
      (data.zip_code && data.zip_code.trim() !== "");

    const hasAddressLines =
      (data.address_line_1 && data.address_line_1.trim() !== "") &&
      (data.address_line_2 && data.address_line_2.trim() !== "");

    if (!hasFullAddress && !hasAddressLines) {
      toast.error("Please provide either (Address + City + State + Zip Code) OR (Address Line 1 + Address Line 2).");
      return false;
    }

    return true;
  };

  const validateEdit = () => {
    if (!data.shipper_name || data.shipper_name.trim() === "") {
      toast.error("Please fill in all required fields.");
      return false;
    }
    return true;
  };

  const handleSubmit = async () => {
    if (isSubmitting) return;

    if (dialogMode === "create") {
      if (!validateCreate()) return;
      await createShipper();
      return;
    }

    if (dialogMode === "edit") {
      if (!validateEdit()) return;
      await updateShipper();
      return;
    }
  };

  const createShipper = async () => {
    setIsSubmitting(true);
    try {
      const token = localStorage.getItem("token");
      const headers = { "Screen-Name": "/Shipper", Authorization: `Bearer ${token}` };

      const payload = {
        shipper_name: data.shipper_name,
        type: data.type,
        phone: data.phone,
        accounting_email: data.accounting_email,
        info_email: data.info_email,
        address: data.address,
        city: data.city,
        state: data.state,
        zip_code: data.zip_code,
        address_line_1: data.address_line_1,
        address_line_2: data.address_line_2,
      };

      await axios.post(API_BASE, payload, { headers });

      toast.success("Shipper created successfully.");
      setIsDialogOpen(false);
      resetForm();

      setCurrentPage(1);
      await fetchShippers(1);
    } catch (error) {
      toast.error(extractErrorMessage(error));
    } finally {
      setIsSubmitting(false);
    }
  };

  const updateShipper = async () => {
    setIsSubmitting(true);
    try {
      const token = localStorage.getItem("token");
      const headers = { "Screen-Name": "/Shipper", Authorization: `Bearer ${token}` };

      const payload = {
        shipper_name: data.shipper_name,
        type: data.type,
        phone: data.phone,
        accounting_email: data.accounting_email,
        info_email: data.info_email,
        address: data.address,
        city: data.city,
        state: data.state,
        zip_code: data.zip_code,
        address_line_1: data.address_line_1,
        address_line_2: data.address_line_2,
      };

      // ✅ padrão REST: POST /shipper/{id}
      try {
        await axios.post(`${API_BASE}/${data.id}`, payload, { headers });
      } catch (e) {
        const status = e?.response?.status;

        // ✅ fallback se sua API estiver usando POST /shipper/{id}
        if (status === 404 || status === 405) {
          await axios.post(`${API_BASE}/${data.id}`, payload, { headers });
        } else {
          throw e;
        }
      }

      toast.success("Shipper updated successfully.");
      setIsDialogOpen(false);
      resetForm();

      await fetchShippers(currentPage);
    } catch (error) {
      toast.error(extractErrorMessage(error));
    } finally {
      setIsSubmitting(false);
    }
  };

  const handleDelete = async (id) => {
    try {
      const token = localStorage.getItem("token");
      const headers = { "Screen-Name": "/Shipper", Authorization: `Bearer ${token}` };

      await axios.delete(`${API_BASE}/${id}`, { headers });

      toast.success("Shipper deleted successfully.");
      await fetchShippers(currentPage);
    } catch (error) {
      toast.error(extractErrorMessage(error));
    }
  };

  const handleEdit = (shipper) => {
    setData({
      id: shipper.id,
      shipper_name: shipper.shipper_name || "",
      type: shipper.type || "",
      phone: shipper.phone || "",
      accounting_email: shipper.accounting_email || "",
      info_email: shipper.info_email || "",
      address: shipper.address || "",
      city: shipper.city || "",
      state: shipper.state || "",
      zip_code: shipper.zip_code || "",
      address_line_1: shipper.address_line_1 || "",
      address_line_2: shipper.address_line_2 || "",
    });
    setDialogMode("edit");
    setIsDialogOpen(true);
  };

  // ✅ pagination UI
  const paginate = (pageNumber) => setCurrentPage(pageNumber);
  const nextPage = () => setCurrentPage((prev) => Math.min(prev + 1, totalPages));
  const prevPage = () => setCurrentPage((prev) => Math.max(prev - 1, 1));

  const clearSearch = () => {
    setSearchQuery("");
    setCurrentPage(1);
  };

  const clearFilters = () => {
    setSearchQuery("");
    setPerPage(20);
    setSortBy("shipper_name");
    setSortDirection("asc");
    setCurrentPage(1);
    toast.info("Filters clean");
  };

  return (
    <AuthenticatedLayout>
      <div className="text-white text-base sm:text-lg md:text-xl lg:text-2xl p-4 sm:p-6 md:p-8">
        <GroupedContainer>
          <InfoSection title="SHIPPERS" description="Shippers overview" />
        </GroupedContainer>

        {/* Filters (universal) */}
        <div className="bg-[#1c1b19] p-4 sm:p-6 rounded-xl shadow-sm text-white border border-zinc-600 mt-4 mb-4">
          <div className="flex justify-between items-center mb-4 gap-3">
            <h2 className="text-lg sm:text-xl font-bold flex items-center gap-2">
              <TbFilter className="text-amber-500 w-5 h-5" />
              Filters
            </h2>

            <div className="flex items-center gap-2">
              <button onClick={clearFilters} className="text-sm text-gray-400 hover:text-white flex items-center gap-1">
                <TbX className="w-4 h-4" /> Clear Filters
              </button>

              <Dialog open={isDialogOpen} onOpenChange={setIsDialogOpen}>
                <DialogTrigger
                  className="text-sm px-4 py-2 bg-amber-600 text-black rounded-lg hover:bg-amber-700 transition-colors flex items-center gap-2"
                  onClick={() => {
                    setDialogMode("create");
                    setIsDialogOpen(true);
                    resetForm();
                  }}
                >
                  <TbPlus className="w-4 h-4" />
                  Add Shipper
                </DialogTrigger>

                <DialogContent className="h-[90vh] overflow-y-auto sm:h-auto sm:max-h-[90vh]">
                  <div>
                    <DialogTitle>{dialogMode === "create" ? "Add Shipper" : "Edit Shipper"}</DialogTitle>

                    <div className="mt-4 space-y-6">
                      <div className="space-y-4">
                        <h3 className="text-lg font-semibold text-amber-500">Basic Information</h3>

                        <div>
                          <label className="block text-sm font-medium text-gray-400 mb-1">
                            Shipper Name <span className="text-red-500">*</span>
                          </label>
                          <input
                            type="text"
                            value={data.shipper_name}
                            onChange={(e) => setData({ ...data, shipper_name: e.target.value })}
                            className="w-full px-4 py-2 bg-transparent text-white border border-zinc-600 rounded-lg focus:outline-none focus:ring-2 focus:ring-amber-500"
                          />
                        </div>

                        <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                          <div>
                            <label className="block text-sm font-medium text-gray-400 mb-1">Type</label>
                            <input
                              type="text"
                              value={data.type}
                              onChange={(e) => setData({ ...data, type: e.target.value })}
                              className="w-full px-4 py-2 bg-transparent text-white border border-zinc-600 rounded-lg focus:outline-none focus:ring-2 focus:ring-amber-500"
                            />
                          </div>

                          <div>
                            <label className="block text-sm font-medium text-gray-400 mb-1">Phone</label>
                            <input
                              type="tel"
                              value={data.phone}
                              onChange={(e) => setData({ ...data, phone: e.target.value })}
                              className="w-full px-4 py-2 bg-transparent text-white border border-zinc-600 rounded-lg focus:outline-none focus:ring-2 focus:ring-amber-500"
                            />
                          </div>

                          <div>
                            <label className="block text-sm font-medium text-gray-400 mb-1">Accounting Email</label>
                            <input
                              type="email"
                              value={data.accounting_email}
                              onChange={(e) => setData({ ...data, accounting_email: e.target.value })}
                              className="w-full px-4 py-2 bg-transparent text-white border border-zinc-600 rounded-lg focus:outline-none focus:ring-2 focus:ring-amber-500"
                            />
                          </div>

                          <div>
                            <label className="block text-sm font-medium text-gray-400 mb-1">Info Email</label>
                            <input
                              type="email"
                              value={data.info_email}
                              onChange={(e) => setData({ ...data, info_email: e.target.value })}
                              className="w-full px-4 py-2 bg-transparent text-white border border-zinc-600 rounded-lg focus:outline-none focus:ring-2 focus:ring-amber-500"
                            />
                          </div>
                        </div>
                      </div>

                      <div className="space-y-4">
                        <h3 className="text-lg font-semibold text-blue-500">Address Options</h3>

                        <div className="text-xs text-white/60">
                          Provide either (Address + City + State + Zip Code) OR (Address Line 1 + Address Line 2).
                        </div>

                        <div>
                          <label className="block text-sm font-medium text-gray-400 mb-1">Address</label>
                          <input
                            type="text"
                            value={data.address}
                            onChange={(e) => setData({ ...data, address: e.target.value })}
                            className="w-full px-4 py-2 bg-transparent text-white border border-zinc-600 rounded-lg focus:outline-none focus:ring-2 focus:ring-amber-500"
                          />
                        </div>

                        <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
                          <div>
                            <label className="block text-sm font-medium text-gray-400 mb-1">City</label>
                            <input
                              type="text"
                              value={data.city}
                              onChange={(e) => setData({ ...data, city: e.target.value })}
                              className="w-full px-4 py-2 bg-transparent text-white border border-zinc-600 rounded-lg focus:outline-none focus:ring-2 focus:ring-amber-500"
                            />
                          </div>

                          <div>
                            <label className="block text-sm font-medium text-gray-400 mb-1">State</label>
                            <input
                              type="text"
                              value={data.state}
                              onChange={(e) => setData({ ...data, state: e.target.value })}
                              className="w-full px-4 py-2 bg-transparent text-white border border-zinc-600 rounded-lg focus:outline-none focus:ring-2 focus:ring-amber-500 uppercase"
                            />
                          </div>

                          <div>
                            <label className="block text-sm font-medium text-gray-400 mb-1">Zip Code</label>
                            <input
                              type="text"
                              value={data.zip_code}
                              onChange={(e) => setData({ ...data, zip_code: e.target.value })}
                              className="w-full px-4 py-2 bg-transparent text-white border border-zinc-600 rounded-lg focus:outline-none focus:ring-2 focus:ring-amber-500"
                            />
                          </div>
                        </div>

                        <div>
                          <label className="block text-sm font-medium text-gray-400 mb-1">Address Line 1</label>
                          <input
                            type="text"
                            value={data.address_line_1}
                            onChange={(e) => setData({ ...data, address_line_1: e.target.value })}
                            className="w-full px-4 py-2 bg-transparent text-white border border-zinc-600 rounded-lg focus:outline-none focus:ring-2 focus:ring-amber-500"
                          />
                        </div>

                        <div>
                          <label className="block text-sm font-medium text-gray-400 mb-1">Address Line 2</label>
                          <input
                            type="text"
                            value={data.address_line_2}
                            onChange={(e) => setData({ ...data, address_line_2: e.target.value })}
                            className="w-full px-4 py-2 bg-transparent text-white border border-zinc-600 rounded-lg focus:outline-none focus:ring-2 focus:ring-amber-500"
                          />
                        </div>
                      </div>

                      <div className="flex justify-end gap-3 pt-2">
                        <SecondaryButton
                          variant="outline"
                          onClick={() => {
                            setIsDialogOpen(false);
                            resetForm();
                          }}
                          className="text-white border-white hover:bg-white/10"
                        >
                          Cancel
                        </SecondaryButton>

                        {dialogMode === "create" ? (
                          <PrimaryButton
                            onClick={handleSubmit}
                            disabled={isSubmitting}
                            className="bg-amber-600 text-black hover:bg-amber-700"
                          >
                            {isSubmitting ? "Saving..." : "Add"}
                          </PrimaryButton>
                        ) : (
                          <PrimaryButton
                            onClick={handleSubmit}
                            disabled={isSubmitting}
                            className="bg-amber-600 text-black hover:bg-amber-700"
                          >
                            {isSubmitting ? "Saving..." : "Update"}
                          </PrimaryButton>
                        )}
                      </div>
                    </div>
                  </div>
                </DialogContent>
              </Dialog>
            </div>
          </div>

          {/* 4 controls like your reference page */}
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4">
            {/* Universal Search */}
            <div className="relative">
              <div className="absolute inset-y-0 left-3 flex items-center pointer-events-none">
                <TbSearch className="text-white text-lg" />
              </div>
              <input
                type="text"
                placeholder="Search name, phone, city, state, address..."
                value={searchQuery}
                onChange={(e) => {
                  setSearchQuery(e.target.value);
                  setCurrentPage(1);
                }}
                className="w-full pl-10 pr-10 bg-transparent placeholder:text-white py-2 sm:py-3 text-sm border text-white rounded-lg focus:outline-none focus:ring-2 focus:ring-amber-500 focus:border-transparent transition-all"
              />
              {searchQuery && (
                <button
                  onClick={clearSearch}
                  className="absolute inset-y-0 right-3 flex items-center text-gray-400 hover:text-white"
                  title="Clear search"
                >
                  <TbX className="w-4 h-4" />
                </button>
              )}
            </div>

            {/* Per Page */}
            <div className="relative">
              <div className="absolute inset-y-0 left-3 flex items-center pointer-events-none">
                <TbChevronDown className="text-white text-lg" />
              </div>
              <select
                value={String(perPage)}
                onChange={(e) => {
                  setPerPage(parseInt(e.target.value));
                  setCurrentPage(1);
                }}
                className="w-full pl-10 pr-12 bg-transparent text-white py-2 sm:py-3 text-sm border rounded-lg
                           focus:outline-none focus:ring-2 focus:ring-amber-500 focus:border-transparent transition-all
                           appearance-none"
              >
                <option value="10" className="bg-[#1c1b19]">10 / page</option>
                <option value="20" className="bg-[#1c1b19]">20 / page</option>
                <option value="50" className="bg-[#1c1b19]">50 / page</option>
                <option value="100" className="bg-[#1c1b19]">100 / page</option>
              </select>
              <div className="absolute inset-y-0 right-3 flex items-center pointer-events-none">
                <TbChevronDown className="text-white text-lg" />
              </div>
            </div>

            {/* Order By */}
            <div className="relative">
              <div className="absolute inset-y-0 left-3 flex items-center pointer-events-none">
                <TbFilter className="text-amber-500 text-lg" />
              </div>
              <select
                value={sortBy}
                onChange={(e) => {
                  setSortBy(e.target.value);
                  setCurrentPage(1);
                }}
                className="w-full pl-10 pr-12 bg-transparent text-white py-2 sm:py-3 text-sm border rounded-lg
                           focus:outline-none focus:ring-2 focus:ring-amber-500 focus:border-transparent transition-all
                           appearance-none"
              >
                <option value="shipper_name" className="bg-[#1c1b19]">Order by: Name</option>
                <option value="phone" className="bg-[#1c1b19]">Order by: Phone</option>
                <option value="city" className="bg-[#1c1b19]">Order by: City</option>
                <option value="state" className="bg-[#1c1b19]">Order by: State</option>
                <option value="type" className="bg-[#1c1b19]">Order by: Type</option>
                <option value="created_at" className="bg-[#1c1b19]">Order by: Created</option>
                <option value="updated_at" className="bg-[#1c1b19]">Order by: Updated</option>
              </select>
              <div className="absolute inset-y-0 right-3 flex items-center pointer-events-none">
                <TbChevronDown className="text-white text-lg" />
              </div>
            </div>

            {/* Direction */}
            <div className="relative">
              <div className="absolute inset-y-0 left-3 flex items-center pointer-events-none">
                <TbChevronDown className="text-white text-lg" />
              </div>
              <select
                value={sortDirection}
                onChange={(e) => {
                  setSortDirection(e.target.value);
                  setCurrentPage(1);
                }}
                className="w-full pl-10 pr-12 bg-transparent text-white py-2 sm:py-3 text-sm border rounded-lg
                           focus:outline-none focus:ring-2 focus:ring-amber-500 focus:border-transparent transition-all
                           appearance-none"
              >
                <option value="asc" className="bg-[#1c1b19]">Direction: ASC</option>
                <option value="desc" className="bg-[#1c1b19]">Direction: DESC</option>
              </select>
              <div className="absolute inset-y-0 right-3 flex items-center pointer-events-none">
                <TbChevronDown className="text-white text-lg" />
              </div>
            </div>
          </div>
        </div>

        {/* Table */}
        <div className="mt-6 overflow-x-auto rounded-xl border border-zinc-600">
          <table className="min-w-full divide-y divide-zinc-600">
            <thead className="bg-[#1c1b19]">
              <tr>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-400 uppercase tracking-wider">Shipper Name</th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-400 uppercase tracking-wider">Type</th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-400 uppercase tracking-wider">Phone</th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-400 uppercase tracking-wider">Accounting Email</th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-400 uppercase tracking-wider">Info Email</th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-400 uppercase tracking-wider">Address</th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-400 uppercase tracking-wider">Actions</th>
              </tr>
            </thead>

            <tbody className="bg-[#1c1b19]/50 divide-y divide-zinc-600">
              {loading ? (
                <tr>
                  <td colSpan="7" className="px-6 py-8 text-center">
                    <div className="flex justify-center">
                      <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-amber-500"></div>
                    </div>
                    <p className="mt-2 text-gray-400">Loading shippers...</p>
                  </td>
                </tr>
              ) : shippers.length > 0 ? (
                shippers.map((shipper) => (
                  <tr key={shipper.id} className="hover:bg-zinc-800/30 transition-colors">
                    <td className="px-6 py-4 whitespace-nowrap text-sm font-medium text-white">{shipper.shipper_name}</td>
                    <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-300">{shipper.type || "-"}</td>
                    <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-300">{shipper.phone || "-"}</td>

                    <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-300">
                      <span className="block max-w-[260px] truncate" title={shipper.accounting_email || ""}>
                        {shipper.accounting_email || "-"}
                      </span>
                    </td>

                    <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-300">
                      <span className="block max-w-[260px] truncate" title={shipper.info_email || ""}>
                        {shipper.info_email || "-"}
                      </span>
                    </td>

                    <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-300">
                      <span className="block max-w-[520px] truncate" title={formatAddress(shipper) || ""}>
                        {formatAddress(shipper) || "-"}
                      </span>
                    </td>

                    <td className="px-6 py-4 whitespace-nowrap">
                      <div className="flex items-center space-x-2">
                        <button
                          onClick={() => handleEdit(shipper)}
                          className="p-2 text-gray-400 hover:text-amber-500 hover:bg-zinc-700/50 rounded-lg transition-colors"
                          title="View / Edit"
                        >
                          <TbEye className="w-4 h-4" />
                        </button>

                        <ConfirmationDialog
                          triggerButton={
                            <button
                              className="p-2 text-gray-400 hover:text-red-500 hover:bg-red-500/20 rounded-lg transition-colors"
                              title="Delete"
                            >
                              <TbTrash className="w-4 h-4" />
                            </button>
                          }
                          confirmText="Delete"
                          confirmAction={() => handleDelete(shipper.id)}
                        >
                          <DialogTitle>Delete this shipper?</DialogTitle>
                          <p className="text-sm text-white leading-relaxed">
                            You're about to delete shipper{" "}
                            <span className="font-semibold">{shipper.shipper_name}</span> and this action cannot be undone.
                            Would you like to continue?
                          </p>
                        </ConfirmationDialog>
                      </div>
                    </td>
                  </tr>
                ))
              ) : (
                <tr>
                  <td colSpan="7" className="px-6 py-8 text-center text-gray-400">
                    <div className="text-gray-400">
                      <TbInfoCircle className="w-10 h-10 mx-auto mb-2 opacity-50" />
                      No shippers found matching your filters.
                    </div>
                  </td>
                </tr>
              )}
            </tbody>
          </table>
        </div>

        {/* Pagination */}
        {paginationMeta.total > 0 && (
          <>
            <div className="flex items-center justify-center space-x-2 mt-6">
              <SecondaryButton
                variant="outline"
                size="sm"
                onClick={prevPage}
                disabled={currentPage === 1}
                className="text-white border-white hover:bg-white/10"
              >
                <ChevronLeft className="h-4 w-4 mr-1" />
                Previous
              </SecondaryButton>

              <div className="flex items-center space-x-1">
                {(() => {
                  let startPage = Math.max(1, currentPage - 2);
                  const endPage = Math.min(totalPages, startPage + 4);

                  if (endPage - startPage < 4 && startPage > 1) {
                    startPage = Math.max(1, endPage - 4);
                  }

                  const pageNumbers = [];
                  for (let i = startPage; i <= endPage; i++) {
                    pageNumbers.push(
                      <SecondaryButton
                        key={i}
                        size="sm"
                        onClick={() => paginate(i)}
                        className={
                          currentPage === i
                            ? "bg-amber-600 text-black hover:bg-white/90"
                            : "text-black border-white hover:bg-white/10"
                        }
                      >
                        {i}
                      </SecondaryButton>
                    );
                  }
                  return pageNumbers;
                })()}
              </div>

              <SecondaryButton
                variant="outline"
                size="sm"
                onClick={nextPage}
                disabled={currentPage === totalPages}
                className="text-white border-white hover:bg-white/10"
              >
                Next
                <ChevronRight className="h-4 w-4 ml-1" />
              </SecondaryButton>
            </div>

            <div className="text-center text-sm text-white/70 mt-2">
              Showing {paginationMeta.from || 0}-{paginationMeta.to || 0} of {paginationMeta.total || 0} shippers
            </div>
          </>
        )}
      </div>
    </AuthenticatedLayout>
  );
}
